import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import BusinessApiService from "../../services/BusinessApiService";
import { getCurrentBusinessId } from "../../services/api";

import {
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Package,
  Truck,
  AlertTriangle,
  RefreshCw,
  BarChart3,
  QrCode,
  Tag as TagIcon,
  MapPin,
  Store as StoreIcon,
  ShieldAlert,
  FileText,
  Upload,
  Award,
  ClipboardCheck,
  X,
  Download,
  CheckCircle,
  Clock,
  FileUp,
} from "lucide-react";

/* =============================== Types ================================ */
interface Product {
  productId: string;
  _id?: string;
  name: string;
  sku: string;
  brand?: string;
  category?: string;
}

interface BusinessStore {
  storeId: string;
  _id?: string;
  storeName: string;
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  isActive?: boolean;
}

type BatchStatus =
  | "created"
  | "production"
  | "quality_check"
  | "approved"
  | "shipped"
  | "delivered"
  | "recalled";

interface Batch {
  batchId: string;
  _id?: string;
  businessId: string;
  productId: string;
  product?: Product | null;
  batchNumber: string;
  name?: string;
  assignedStoreId?: string | null;
  assignedStore?: BusinessStore | null;
  status: BatchStatus;
  quantity: number;
  manufacturingDate?: string;
  expiryDate?: string | null;
  location?: string;
  notes?: string;
  description?: string;
  totalTags?: number;
  activatedTags?: number;
  pendingTags?: number;
  createdAt?: string;
  updatedAt?: string;
  blockchain?: {
    traceUrl?: string | null;
    contentHash?: string | null;
    anchorHash?: string | null;
    transactionHash?: string | null;
    blockNumber?: number | null;
    status?: 'none' | 'pending' | 'anchored' | 'failed';
    anchoredAt?: string | null;
    fetchedAt?: string | null;
    confirmed?: boolean;
    error?: string | null;
  };
}

interface CreateForm {
  name: string;
  batchNumber: string;
  productId: string;
  assignedStoreId: string;
  quantity: string;
  manufacturingDate: string;
  expiryDate: string;
  warrantyMonths?: string;
  location: string;
  notes: string;
  traceUrl: string;          // ⛓ Blockchain trace URL (locked on chain at activation)
  status: BatchStatus;
}

interface Certificate {
  _id: string;
  name: string;
  type: string;
  certificateNumber?: string;
  issuedBy?: string;
  issueDate?: string;
  expiryDate?: string;
  fileUrl: string;
  verified: boolean;
  notes?: string;
  uploadedAt: string;
}

interface Inspection {
  _id: string;
  inspector?: string;
  inspectionDate?: string;
  result?: string;
  fileUrl: string;
  notes?: string;
  uploadedAt: string;
}

interface CertUploadForm {
  name: string;
  type: string;
  certificateNumber: string;
  issuedBy: string;
  issueDate: string;
  expiryDate: string;
  notes: string;
}

interface InspectionUploadForm {
  inspector: string;
  inspectionDate: string;
  result: string;
  notes: string;
}

/* ============================== Helpers =============================== */
const safeIso = (val: any): string => {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 16);
};

const statusColor = (s: BatchStatus) => {
  switch (s) {
    case "created":
      return "bg-gray-100 text-gray-800";
    case "production":
      return "bg-indigo-100 text-indigo-800";
    case "quality_check":
      return "bg-yellow-100 text-yellow-800";
    case "approved":
      return "bg-green-100 text-green-800";
    case "shipped":
      return "bg-purple-100 text-purple-800";
    case "delivered":
      return "bg-emerald-100 text-emerald-800";
    case "recalled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const toIso = (dtLocal: string) => (dtLocal ? new Date(dtLocal).toISOString() : "");

const addMonths = (iso: string, months: number) => {
  const d = new Date(iso);
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  if (d.getDate() < day) d.setDate(0);
  return d.toISOString();
};

const ensureExpiryAfterMfg = (mfgIso: string, expIso: string) => {
  if (!mfgIso || !expIso) return;
  const m = new Date(mfgIso).getTime();
  const e = new Date(expIso).getTime();
  if (e <= m) throw new Error("Expiry must be after Manufacturing date");
};

const fmtDate = (iso?: string | null) => { if (!iso) return 'N/A'; const d = new Date(iso); return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('en-US', {year:'2-digit',month:'2-digit',day:'2-digit'}); };

/* =============================== Page ================================= */
const BatchesPage: React.FC = () => {
  // PATCH: Ensure bizId is always a string
  const bizRaw = getCurrentBusinessId();
  const bizId = typeof bizRaw === "object" && bizRaw !== null
    ? (bizRaw as any).businessId || ""
    : bizRaw || "";

  // data
  const [batches, setBatches] = useState<Batch[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [stores, setStores] = useState<BusinessStore[]>([]);

  // ui state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);

  // filters
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | BatchStatus>("all");
  const [filterProduct, setFilterProduct] = useState("all");
  const [filterStore, setFilterStore] = useState("all");
  const [searchParams] = useSearchParams();

  // modals
  const [showCreate, setShowCreate] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // certificate & inspection state
  const [detailTab, setDetailTab] = useState<"info" | "quality">("info");
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [showCertUpload, setShowCertUpload] = useState(false);
  const [showInspUpload, setShowInspUpload] = useState(false);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [inspFile, setInspFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [certForm, setCertForm] = useState<CertUploadForm>({
    name: "", type: "ISO", certificateNumber: "", issuedBy: "",
    issueDate: "", expiryDate: "", notes: "",
  });
  const [inspForm, setInspForm] = useState<InspectionUploadForm>({
    inspector: "", inspectionDate: "", result: "pass", notes: "",
  });

  // create form + expiry/warranty toggle
  const [expiryMode, setExpiryMode] = useState<"expiry" | "warranty">("expiry");
  const [form, setForm] = useState<CreateForm>({
    name: "",
    batchNumber: "",
    productId: "",
    assignedStoreId: "",
    quantity: "1",
    manufacturingDate: "",
    expiryDate: "",
    warrantyMonths: "",
    location: "",
    notes: "",
    traceUrl: "",
    status: "created",
  });

  /* --------------------------- Load Products --------------------------- */
  const loadProducts = useCallback(async () => {
    try {
      if (!bizId) {
        console.log("No business ID available");
        return;
      }

      setLoadingProducts(true);
      const token = BusinessApiService.getBusinessToken();
      if (!token) {
        setProducts([]);
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL}/products?businessId=${bizId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const json = await res.json();

      let raw: any[] = [];
      if (json?.data?.products) {
        raw = json.data.products;
      } else if (json?.products) {
        raw = json.products;
      } else if (json?.data && Array.isArray(json.data)) {
        raw = json.data;
      } else if (Array.isArray(json)) {
        raw = json;
      }

      // ✅ Helper to extract string ID from ObjectId or string
      const extractId = (val: any): string => {
        if (!val) return "";
        if (typeof val === "string") return val;
        if (val.$oid) return val.$oid;
        if (val._id) return String(val._id);
        if (val.toString && typeof val.toString === "function") {
          const s = val.toString();
          if (s && s !== "[object Object]") return s;
        }
        return "";
      };

      const normalizedProducts = raw
        .map((p: any) => p?._doc || p)
        .map((p: any) => ({
          productId: extractId(p.productId) || extractId(p._id),
          _id: extractId(p._id),
          name: p.name || "Unnamed Product",
          sku: p.sku || "NO-SKU",
          brand: p.brand,
          category: p.category,
          country: p.country || '',
          parentSku: p.parentSku || '',
        }));

      setProducts(normalizedProducts);

    } catch (e: any) {
      setError(`Failed to load products: ${e.message}`);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [bizId]);

  /* ---------------------------- Load Stores ---------------------------- */
  const loadStores = useCallback(async () => {
    try {
      if (!bizId) {
        return;
      }

      setLoadingStores(true);

      const token = BusinessApiService.getBusinessToken();
      if (!token) {
        setStores([]);
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL}/stores?businessId=${bizId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const json = await res.json();

      let raw: any[] = [];
      if (json?.data?.stores) {
        raw = json.data.stores;
      } else if (json?.stores) {
        raw = json.stores;
      } else if (json?.data && Array.isArray(json.data)) {
        raw = json.data;
      } else if (Array.isArray(json)) {
        raw = json;
      }

      // ✅ Helper to extract string ID from ObjectId or string  
      const extractStoreId = (val: any): string => {
        if (!val) return "";
        if (typeof val === "string") return val;
        if (val.$oid) return val.$oid;
        if (val._id) return String(val._id);
        if (val.toString && typeof val.toString === "function") {
          const s = val.toString();
          if (s && s !== "[object Object]") return s;
        }
        return "";
      };

      const normalizedStores = raw
        .map((s: any) => s?._doc || s)
        .map((s: any) => {
          const idStr = extractStoreId(s._id);
          const storeIdStr = extractStoreId(s.storeId) || idStr;
          return {
            storeId: storeIdStr,
            _id: idStr,
            storeName: s.storeName || s.name || ('Store ' + storeIdStr.slice(-6)),
            name: s.name,
            address: s.address,
            city: s.city || (typeof s.address === 'object' ? s.address?.city : '') || '',
            state: s.state || '',
            isActive: s.isActive !== false,
          };
        })
        .filter((s: any, i: number, arr: any[]) =>
          arr.findIndex((x: any) => x.storeId === s.storeId) === i
        );

      setStores(normalizedStores);

    } catch (e: any) {
      setError(`Failed to load stores: ${e.message}`);
      setStores([]);
    } finally {
      setLoadingStores(false);
    }
  }, [bizId]);

  /* ---------------------------- Load Batches --------------------------- */
  const loadBatches = useCallback(async () => {
    try {
      if (!bizId) return;
      setLoading(true);
      setError(null);

      const token = BusinessApiService.getBusinessToken();
      if (!token) {
        throw new Error("No business token available");
      }

      // ✅ Filter by productId if set
      const batchUrl = new URL(`${process.env.REACT_APP_API_URL}/batches`);
      batchUrl.searchParams.set('businessId', bizId);
      if (filterProduct && filterProduct !== 'all') {
        batchUrl.searchParams.set('productId', filterProduct);
      }
      const res = await fetch(batchUrl.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const json = await res.json();

      if (!json?.success) {
        throw new Error(json?.error || json?.message || "Load failed");
      }

      const raw: any[] = json?.data?.batches || json?.batches || json?.data || [];

      const norm: Batch[] = raw.map((rawB: any) => {
        const b = rawB?._doc || rawB;
        
        // ✅ FIX: Handle ObjectId objects — extract string ID properly
        const extractId = (val: any): string => {
          if (!val) return "";
          if (typeof val === "string") return val;
          if (val.$oid) return val.$oid;
          if (val._id) return String(val._id);
          if (val.toString && typeof val.toString === "function") {
            const s = val.toString();
            // Avoid "[object Object]"
            if (s && s !== "[object Object]") return s;
          }
          return "";
        };
        
        const prodId = extractId(b.productId);
        
        // Match product by productId string, _id, or ObjectId
        const localProduct =
          products.find((p) => p.productId === prodId) ||
          (prodId ? products.find((p) => p._id === prodId) : null) ||
          null;
        const product = localProduct || (b.productInfo ? {
          productId: b.productInfo.productId || prodId,
          _id: b.productInfo._id || prodId,
          name: b.productInfo.name || b.productName,
          sku: b.productInfo.sku || b.productSku || "",
          brand: b.productInfo.brand || "",
        } : null);

        const storeId = extractId(b.assignedStoreId);
        const assignedStore = storeId 
          ? stores.find((s) => s.storeId === storeId || s._id === storeId) || null 
          : null;

        return {
          batchId: b.batchId || b._id,
          _id: b._id,
          businessId: b.businessId || bizId,
          productId: product?.productId || prodId || "",
          product,
          batchNumber: b.batchNumber || b.name || "",
          name: b.name || b.batchNumber || "",
          assignedStoreId: storeId || null,
          assignedStore,
          status: (b.status as BatchStatus) || "created",
          quantity: Number(b.quantity || 0),
          manufacturingDate: b.manufacturingDate || b.manufacturing?.date,
          expiryDate: b.expiryDate || b.expiry?.expiryDate || null,
          location: b.location || b.manufacturing?.location,
          notes: b.notes || b.description,
          description: b.description,
          totalTags: b.stats?.totalTags ?? b.totalTags ?? 0,
          activatedTags: b.stats?.activatedTags ?? b.activatedTags ?? 0,
          pendingTags: b.stats?.pendingTags ?? b.pendingTags ?? 0,
          createdAt: b.createdAt,
          updatedAt: b.updatedAt,
        };
      });

      setBatches(norm);

      // ✅ FIX: Resolve unmatched products via API lookup
      // Some batches store productId as MongoDB ObjectId hex string (e.g. "69c082b9...")
      // which doesn't match any product.productId ("PROD-xxx") or product._id ("")
      const unresolvedBatches = norm.filter(b => !b.product && b.productId && b.productId.length > 10);
      if (unresolvedBatches.length > 0) {
        const token = BusinessApiService.getBusinessToken();
        const uniqueIds = Array.from(new Set(unresolvedBatches.map(b => b.productId)));
        
        const resolvedProducts: Record<string, Product> = {};
        
        for (const pid of uniqueIds) {
          try {
            // Try fetching product by ObjectId
            const pRes = await fetch(`${process.env.REACT_APP_API_URL}/products/${encodeURIComponent(pid)}`, {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            });
            if (pRes.ok) {
              const pJson = await pRes.json();
              const pData = pJson?.data?.product || pJson?.product || pJson?.data || pJson;
              if (pData?.name) {
                resolvedProducts[pid] = {
                  productId: pData.productId || pid,
                  _id: pid,
                  name: pData.name,
                  sku: pData.sku || "N/A",
                  brand: pData.brand,
                };
              }
            }
          } catch { /* skip */ }
        }

        // Update batches with resolved products
        if (Object.keys(resolvedProducts).length > 0) {
          setBatches(prev => prev.map(b => {
            if (!b.product && resolvedProducts[b.productId]) {
              const rp = resolvedProducts[b.productId];
              return { ...b, product: rp, productId: rp.productId || b.productId };
            }
            return b;
          }));
        }
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load batches");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [bizId, products, stores]);

  /* ----------------------------- Boot sequence ----------------------------- */
  useEffect(() => {
    const boot = async () => {
      if (bizId) {
        await Promise.all([loadProducts(), loadStores()]);
      }
    };
    void boot();
  }, [bizId, loadProducts, loadStores]);

 useEffect(() => {
  if (bizId && products.length > 0 && stores.length > 0) {
    void loadBatches();
  }
}, [bizId, products.length, stores.length, loadBatches]);

  useEffect(() => {
    void loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // ✅ Read productId from URL params on mount
  useEffect(() => {
    const urlProductId = searchParams.get("productId");
    if (urlProductId && urlProductId !== "all") {
      setFilterProduct(urlProductId);
    }
  }, [searchParams]);

  // ✅ Re-fetch batches from server when product filter changes
  useEffect(() => {
    if (bizId) void loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProduct]);

  /* ------------------------------ Form helpers ------------------------------ */
  const resetForm = () => {
    setForm({
      name: "",
      batchNumber: "",
      productId: "",
      assignedStoreId: "",
      quantity: "1",
      manufacturingDate: "",
      expiryDate: "",
      warrantyMonths: "",
      location: "",
      notes: "",
      traceUrl: "",
      status: "created",
    });
    setExpiryMode("expiry");
  };

  /* ------------------------------ Create/Edit flow ------------------------------ */
  const createBatch = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!bizId) throw new Error("No business ID");

      // validations
      if (!form.productId) throw new Error("Please select a product");
      if (!form.assignedStoreId) throw new Error("Please select an assigned store");
      if (!form.name.trim()) throw new Error("Please enter batch name");
      if (!form.manufacturingDate) throw new Error("Please set manufacturing date");

      const manufacturingDateIso = toIso(form.manufacturingDate);

      let expiryIso = "";
      if (expiryMode === "expiry") {
        if (!form.expiryDate) throw new Error("Please set expiry date or switch to warranty mode");
        expiryIso = toIso(form.expiryDate);
      } else {
        const months = Number(form.warrantyMonths || 0);
        if (!months || months < 1) throw new Error("Please enter warranty months (>=1) or switch to expiry mode");
        expiryIso = addMonths(manufacturingDateIso, months);
      }

      ensureExpiryAfterMfg(manufacturingDateIso, expiryIso);

      // PATCH: Remove batchNumber from payload if backend does not allow it
      // Resolve MongoDB _id from custom string IDs
      const selectedProduct = products.find((p) => p.productId === form.productId);
      const selectedStore = stores.find((s) => s.storeId === form.assignedStoreId);
      // Always use string IDs — never send ObjectId objects
      const resolvedProductId = String(selectedProduct?.productId || form.productId || '');
      const resolvedStoreId = String(selectedStore?.storeId || form.assignedStoreId || '');

      const payload: any = {
        businessId: bizId,
        name: form.name.trim(),
        // batchNumber: form.batchNumber.trim() || form.name.trim(), // REMOVE if backend does NOT allow
        productId: resolvedProductId,
        assignedStoreId: resolvedStoreId,
        quantity: Math.max(1, Number(form.quantity || 1)),
        status: form.status,
        manufacturingDate: manufacturingDateIso,
        expiryDate: expiryIso,
        location: form.location.trim() || undefined,
        description: form.notes.trim() || undefined,
        traceUrl: form.traceUrl.trim() || undefined,    // ⛓ Blockchain trace URL
        manufacturing: {
          date: manufacturingDateIso,
          location: form.location.trim() || undefined,
        },
      };
      // If backend expects batchNumber, uncomment next line:
      // payload.batchNumber = form.batchNumber.trim() || form.name.trim();

      const token = BusinessApiService.getBusinessToken();
      // ✅ Filter by productId if set
      const batchUrl = new URL(`${process.env.REACT_APP_API_URL}/batches`);
      batchUrl.searchParams.set('businessId', bizId);
      if (filterProduct && filterProduct !== 'all') {
        batchUrl.searchParams.set('productId', filterProduct);
      }
      const res = await fetch(batchUrl.toString(), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();

      if (!res.ok || !json?.success) {
        throw new Error(json?.error || json?.message || `HTTP ${res.status}`);
      }

      setShowCreate(false);
      resetForm();
      setRefreshKey((k) => k + 1);

    } catch (e: any) {
      alert(e?.message || "Failed to create batch");
    }
  };

  const editBatch = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!bizId || !selectedBatch) throw new Error("Missing batch or business ID");

      // validations
      if (!form.productId) throw new Error("Please select a product");
      if (!form.assignedStoreId) throw new Error("Please select an assigned store");
      if (!form.name.trim()) throw new Error("Please enter batch name");

      const updates: any = {
        name: form.name.trim(),
        assignedStoreId: String(stores.find((s) => s.storeId === form.assignedStoreId)?.storeId || form.assignedStoreId || ''),
        quantity: Math.max(1, Number(form.quantity || 1)),
        location: form.location.trim() || undefined,
        description: form.notes.trim() || undefined,
        status: form.status,
      };

      // ⛓ Only allow traceUrl edit when batch is still in 'created' state
      // After activation, URL is locked on blockchain — backend will reject
      if (selectedBatch.status === 'created') {
        updates.traceUrl = form.traceUrl.trim() || null;
      }

      const token = BusinessApiService.getBusinessToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/batches/${encodeURIComponent(selectedBatch.batchId)}?businessId=${bizId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      const json = await res.json();

      if (!res.ok || !json?.success) {
        throw new Error(json?.error || json?.message || `HTTP ${res.status}`);
      }

      setShowEditModal(false);
      setSelectedBatch(null);
      resetForm();
      setRefreshKey((k) => k + 1);

    } catch (e: any) {
      alert(e?.message || "Failed to update batch");
    }
  };

  /* ------------------------------ Update flow ------------------------------ */
  const handleStatusUpdate = async (batchId: string, newStatus: BatchStatus) => {
    if (!window.confirm(`Update status to "${newStatus}"?`)) return;
    try {
      if (!bizId) throw new Error("No business ID");
      const token = BusinessApiService.getBusinessToken();

      const res = await fetch(`${process.env.REACT_APP_API_URL}/batches/${encodeURIComponent(batchId)}/status?businessId=${bizId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const json = await res.json();

      if (!res.ok || !json?.success) {
        throw new Error(json?.error || json?.message || `HTTP ${res.status}`);
      }

      setRefreshKey((k) => k + 1);

    } catch (e: any) {
      alert(e?.message || "Status update failed");
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const assignStore = async (batchId: string, storeId: string) => {
    try {
      if (!bizId) throw new Error("No business ID");
      const token = BusinessApiService.getBusinessToken();

      const res = await fetch(`${process.env.REACT_APP_API_URL}/batches/${encodeURIComponent(batchId)}?businessId=${bizId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ assignedStoreId: storeId ? String(stores.find((s) => s.storeId === storeId)?.storeId || storeId) : null }),
      });

      const json = await res.json();

      if (!res.ok || !json?.success) {
        throw new Error(json?.error || json?.message || `HTTP ${res.status}`);
      }

      // Optimistically update local state so dropdown shows new value immediately
      setBatches((prev) => prev.map((batch) =>
        batch.batchId === batchId
          ? { ...batch, assignedStoreId: storeId || null, assignedStore: stores.find((s) => s.storeId === storeId) || null }
          : batch
      ));
      setRefreshKey((k) => k + 1);

    } catch (e: any) {
      alert(e?.message || "Store assignment failed");
    }
  };

  const deleteBatch = async (batchId: string) => {
    if (!window.confirm("Delete this batch? This cannot be undone.")) return;
    try {
      if (!bizId) throw new Error("No business ID");
      const token = BusinessApiService.getBusinessToken();

      const res = await fetch(`${process.env.REACT_APP_API_URL}/batches/${encodeURIComponent(batchId)}?businessId=${bizId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const json = await res.json();

      if (!res.ok || !json?.success) {
        throw new Error(json?.error || json?.message || `HTTP ${res.status}`);
      }

      alert(`Batch ${batchId} deleted successfully`);
      setRefreshKey((k) => k + 1);

    } catch (e: any) {
      alert(e?.message || "Delete failed");
    }
  };

  /* ---------------------- Certificate & Inspection APIs ---------------------- */
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${BusinessApiService.getBusinessToken()}`,
  });

  const loadCertificates = async (batchMongoId: string) => {
    try {
      setLoadingDocs(true);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/batches/${batchMongoId}/certificates`, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      });
      const json = await res.json();
      if (json?.success) {
        setCertificates(json.data?.certificates || []);
      }
    } catch (e) {
      console.error("Load certificates error:", e);
    } finally {
      setLoadingDocs(false);
    }
  };

  const loadInspections = async (batchMongoId: string) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/batches/${batchMongoId}/inspections`, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      });
      const json = await res.json();
      if (json?.success) {
        setInspections(json.data?.inspections || []);
      }
    } catch (e) {
      console.error("Load inspections error:", e);
    }
  };

  const uploadCertificate = async () => {
    if (!certFile || !selectedBatch?._id) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", certFile);
      fd.append("name", certForm.name);
      fd.append("type", certForm.type);
      if (certForm.certificateNumber) fd.append("certificateNumber", certForm.certificateNumber);
      if (certForm.issuedBy) fd.append("issuedBy", certForm.issuedBy);
      if (certForm.issueDate) fd.append("issueDate", new Date(certForm.issueDate).toISOString());
      if (certForm.expiryDate) fd.append("expiryDate", new Date(certForm.expiryDate).toISOString());
      if (certForm.notes) fd.append("notes", certForm.notes);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/batches/${selectedBatch._id}/certificates`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: fd,
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Upload failed");

      alert("Certificate uploaded successfully!");
      setShowCertUpload(false);
      setCertFile(null);
      setCertForm({ name: "", type: "ISO", certificateNumber: "", issuedBy: "", issueDate: "", expiryDate: "", notes: "" });
      await loadCertificates(selectedBatch._id);
    } catch (e: any) {
      alert(e?.message || "Certificate upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadInspection = async () => {
    if (!inspFile || !selectedBatch?._id) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", inspFile);
      if (inspForm.inspector) fd.append("inspector", inspForm.inspector);
      if (inspForm.inspectionDate) fd.append("inspectionDate", new Date(inspForm.inspectionDate).toISOString());
      fd.append("result", inspForm.result);
      if (inspForm.notes) fd.append("notes", inspForm.notes);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/batches/${selectedBatch._id}/inspections`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: fd,
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Upload failed");

      alert("Inspection report uploaded successfully!");
      setShowInspUpload(false);
      setInspFile(null);
      setInspForm({ inspector: "", inspectionDate: "", result: "pass", notes: "" });
      await loadInspections(selectedBatch._id);
    } catch (e: any) {
      alert(e?.message || "Inspection upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteCertificate = async (certId: string) => {
    if (!selectedBatch?._id || !window.confirm("Delete this certificate?")) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/batches/${selectedBatch._id}/certificates/${certId}`, {
        method: "DELETE",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Delete failed");
      await loadCertificates(selectedBatch._id);
    } catch (e: any) {
      alert(e?.message || "Delete failed");
    }
  };

  const deleteInspection = async (reportId: string) => {
    if (!selectedBatch?._id || !window.confirm("Delete this inspection report?")) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/batches/${selectedBatch._id}/inspections/${reportId}`, {
        method: "DELETE",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Delete failed");
      await loadInspections(selectedBatch._id);
    } catch (e: any) {
      alert(e?.message || "Delete failed");
    }
  };

  const toggleCertVerified = async (certId: string) => {
    if (!selectedBatch?._id) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/batches/${selectedBatch._id}/certificates/${certId}/verify`, {
        method: "PUT",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Toggle failed");
      await loadCertificates(selectedBatch._id);
    } catch (e: any) {
      alert(e?.message || "Verification toggle failed");
    }
  };

  // Load docs when batch detail opens on Quality tab
  useEffect(() => {
    if (selectedBatch?._id && !showEditModal && detailTab === "quality") {
      loadCertificates(selectedBatch._id);
      loadInspections(selectedBatch._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatch?._id, showEditModal, detailTab]);

  /* -------------------------------- Filters ------------------------------- */
  const filtered = useMemo(() => {
    return batches.filter((b) => {
      const s = search.toLowerCase();
      const matchesSearch =
        b.name?.toLowerCase().includes(s) ||
        b.batchNumber?.toLowerCase().includes(s) ||
        b.product?.name?.toLowerCase().includes(s) ||
        b.assignedStore?.storeName?.toLowerCase().includes(s) ||
        b.location?.toLowerCase().includes(s);
      const matchesStatus = filterStatus === "all" || b.status === filterStatus;
      // ✅ Normalize IDs for comparison (handles ObjectId vs string)
      const normId = (id: any) => {
        if (!id) return '';
        if (typeof id === 'string') return id;
        if (id.$oid) return id.$oid;
        return String(id);
      };
      const matchesProd = filterProduct === "all" || 
        normId(b.productId) === normId(filterProduct) ||
        normId(b.product?.productId) === normId(filterProduct) ||
        normId(b.product?._id) === normId(filterProduct);
      const matchesStore =
        filterStore === "all" ||
        (filterStore === "unassigned" && !b.assignedStoreId) ||
        b.assignedStoreId === filterStore;
      return matchesSearch && matchesStatus && matchesProd && matchesStore;
    });
  }, [batches, search, filterStatus, filterProduct, filterStore]);

  const stats = useMemo(
    () => ({
      total: batches.length,
      assigned: batches.filter((b) => b.assignedStoreId).length,
      unassigned: batches.filter((b) => !b.assignedStoreId).length,
      production: batches.filter((b) => b.status === "production").length,
      shipped: batches.filter((b) => b.status === "shipped").length,
      recalled: batches.filter((b) => b.status === "recalled").length,
      totalTags: batches.reduce((n, b) => n + (b.totalTags || 0), 0),
      activatedTags: batches.reduce((n, b) => n + (b.activatedTags || 0), 0),
    }),
    [batches]
  );

  /* --------------------------------- UI ---------------------------------- */
  if (loading && batches.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-2 text-gray-600">Loading batches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Batch Management</h1>
          <p className="text-gray-600">Create batches, assign stores, and manage batch operations</p>
          {error && <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">⚠️ {error}</div>}
          {loadingProducts && <div className="mt-1 text-sm text-blue-600">Loading products...</div>}
          {loadingStores && <div className="mt-1 text-sm text-blue-600">Loading stores...</div>}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setRefreshKey((k) => k + 1)}
            className="bg-white border px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" /> New Batch
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
        <StatCard label="Total Batches" value={stats.total} icon={<Package className="text-blue-600" />} />
        <StatCard label="Assigned" value={stats.assigned} icon={<StoreIcon className="text-green-600" />} />
        <StatCard label="Unassigned" value={stats.unassigned} icon={<MapPin className="text-orange-600" />} />
        <StatCard label="In Production" value={stats.production} icon={<BarChart3 className="text-yellow-600" />} />
        <StatCard label="Shipped" value={stats.shipped} icon={<Truck className="text-purple-600" />} />
        <StatCard label="Recalled" value={stats.recalled} icon={<AlertTriangle className="text-red-600" />} />
        <StatCard label="Total Tags" value={stats.totalTags.toLocaleString()} icon={<TagIcon className="text-cyan-600" />} />
        <StatCard label="Activated Tags" value={stats.activatedTags.toLocaleString()} icon={<QrCode className="text-emerald-600" />} />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-6 border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search batches, products, stores..."
                className="pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-full md:w-72"
              />
            </div>
            <div className="flex items-center gap-2 text-gray-500">
              <Filter className="w-4 h-4" /> Filters
            </div>
            <select value={filterStore} onChange={(e) => setFilterStore(e.target.value)} className="px-3 py-2 border rounded-lg">
              <option value="all">All Stores</option>
              <option value="unassigned">Unassigned</option>
              {stores
                .filter((s) => s.isActive)
                .map((s) => (
                  <option key={s.storeId} value={s.storeId}>
                    {s.storeName}
                    {s.city ? ` (${s.city})` : ""}
                  </option>
                ))}
            </select>
            <select value={filterProduct} onChange={(e) => setFilterProduct(e.target.value)} className="px-3 py-2 border rounded-lg">
              <option value="all">All Products</option>
              {products.map((p) => (
                <option key={p.productId} value={p.productId}>
                  {p.name} ({p.sku})
                </option>
              ))}
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)} className="px-3 py-2 border rounded-lg">
              <option value="all">All Status</option>
              {(["created", "production", "quality_check", "approved", "shipped", "delivered", "recalled"] as BatchStatus[]).map(
                (s) => (
                  <option key={s} value={s}>
                    {s.replace("_", " ")}
                  </option>
                )
              )}
            </select>
          </div>
          <div className="text-sm text-gray-600">
            Showing {filtered.length} of {batches.length} batches
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left font-medium px-4 py-3">Batch</th>
                <th className="text-left font-medium px-4 py-3">Product</th>
                <th className="text-left font-medium px-4 py-3">Assigned Store</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3">Qty</th>
                <th className="text-left font-medium px-4 py-3">MFG</th>
                <th className="text-left font-medium px-4 py-3">Expiry</th>
                <th className="text-left font-medium px-4 py-3">Tags</th>
                <th className="text-left font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.batchId} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{b.name}</div>
                    <div className="text-gray-500">#{b.batchNumber}</div>
                    <div className="text-gray-400 text-xs font-mono">{b.batchId}</div>
                  </td>
                  <td className="px-4 py-3">
                    {b.product ? (
                      <>
                        <div className="text-gray-900 font-medium">{b.product.name}</div>
                        <div className="text-xs text-gray-500">SKU: {b.product.sku}</div>
                        <div className="text-xs text-gray-400">ID: {b.productId}</div>
                      </>
                    ) : (
                      <div>
                        <div className="text-orange-600 text-sm flex items-center gap-1">
                          <Package className="w-3.5 h-3.5" />
                          {b.productId || "Not assigned"}
                        </div>
                        {b.productId && (
                          <div className="text-xs text-gray-400 mt-0.5">Product not found</div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {b.assignedStore ? (
                      <div>
                        <div className="text-gray-900 flex items-center text-sm">
                          <StoreIcon className="w-4 h-4 mr-1 text-green-600 flex-shrink-0" /> 
                          <span className="truncate max-w-[140px]">{b.assignedStore.storeName}</span>
                        </div>
                        {b.assignedStore.city && (
                          <div className="text-xs text-gray-500 ml-5">{b.assignedStore.city}</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-orange-500 flex items-center text-sm">
                        <MapPin className="w-4 h-4 mr-1 flex-shrink-0" /> Unassigned
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor(b.status)}`}>
                      {b.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">{b.quantity?.toLocaleString?.() ?? b.quantity}</td>
                  <td className="px-4 py-3">{fmtDate(b.manufacturingDate)}</td>
                  <td className="px-4 py-3">{fmtDate(b.expiryDate)}</td>
                  <td className="px-4 py-3">
                    <div className="text-gray-800 flex items-center gap-1">
                      <QrCode className="w-4 h-4" />
                      {(b.activatedTags ?? 0)} / {(b.totalTags ?? 0)}
                    </div>
                    <div className="text-xs text-gray-500">{b.pendingTags ?? 0} pending</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2 items-center">
                      <button
                        onClick={() => setSelectedBatch(b)}
                        className="px-2 py-1 border rounded hover:bg-gray-50"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedBatch(b);
                          setForm({
                            name: b.name || "",
                            batchNumber: b.batchNumber || "",
                            productId: b.productId || "",
                            assignedStoreId: b.assignedStoreId || "",
                            quantity: String(b.quantity || 1),
                            manufacturingDate: safeIso(b.manufacturingDate),
                            expiryDate: safeIso(b.expiryDate),
                            location: b.location || "",
                            notes: b.notes || "",
                            traceUrl: b.blockchain?.traceUrl || "",
                            status: b.status,
                            warrantyMonths: "",
                          });
                          setShowEditModal(true);
                        }}
                        className="px-2 py-1 border rounded text-blue-700 hover:bg-blue-50"
                        title="Edit Batch"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(b.batchId, "approved")}
                        className="px-2 py-1 border rounded text-green-700 hover:bg-green-50"
                        title="Approve"
                        disabled={b.status === "approved"}
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(b.batchId, "shipped")}
                        className="px-2 py-1 border rounded text-purple-700 hover:bg-purple-50"
                        title="Ship"
                        disabled={b.status === "shipped" || b.status === "delivered"}
                      >
                        <Truck className="w-4 h-4" />
                      </button>
                      <Link
                        to={`/business/tags?batchId=${encodeURIComponent(b.batchId)}&productId=${encodeURIComponent(
                          b.product?.productId || b.productId || ''
                        )}&storeId=${encodeURIComponent(String(b.assignedStore?.storeId || b.assignedStoreId || ""))}`}
                        className="px-2 py-1 border rounded hover:bg-gray-50 inline-flex items-center gap-1"
                        title="Manage tags"
                      >
                        <QrCode className="w-4 h-4" /> <span className="text-xs">Tags</span>
                      </Link>
                      <Link
                        to={`/business/batches/${encodeURIComponent(b.batchId)}/activate?productId=${encodeURIComponent(
                          b.product?.productId || b.productId || ""
                        )}&storeId=${encodeURIComponent(String(b.assignedStore?.storeId || b.assignedStoreId || ""))}`}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-lg border hover:bg-zinc-50"
                        title="Activate tags batch"
                      >
                        <QrCode size={16} /> <span className="text-xs">Activate</span>
                      </Link>
                      <Link
                        to={`/business/supply-chain?batchId=${encodeURIComponent(b.batchId)}&productId=${encodeURIComponent(
                          b.product?.productId || b.productId || ''
                        )}`}
                        className="px-2 py-1 border rounded hover:bg-gray-50 inline-flex items-center gap-1"
                        title="Supply chain"
                      >
                        <Truck className="w-4 h-4" /> <span className="text-xs">Supply</span>
                      </Link>
<Link
  to={`/business/recall-batch?batchId=${encodeURIComponent(b.batchId)}&batchName=${encodeURIComponent(b.name || '')}&businessId=${encodeURIComponent(getCurrentBusinessId() || '')}`}
  className="px-2 py-1 border rounded hover:bg-gray-50 inline-flex items-center gap-1"
  title="Recall control"
>
  <ShieldAlert className="w-4 h-4" /> <span className="text-xs">Recall</span>
</Link>
                      <button
                        onClick={() => deleteBatch(b.batchId)}
                        className="px-2 py-1 border rounded text-red-600 hover:bg-red-50"
                        title="Delete Batch"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No batches found</h3>
            <p className="text-gray-600 mb-6">
              {search || filterStatus !== "all" || filterProduct !== "all" || filterStore !== "all"
                ? "Try adjusting your search or filters."
                : "Get started by creating your first batch."}
            </p>
            <button
              onClick={() => setShowCreate(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" /> Create First Batch
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreate || showEditModal) && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-xl font-semibold">
                {showEditModal ? `Edit Batch: ${selectedBatch?.name}` : "Create Batch"}
              </h3>
              <button 
                className="p-2 rounded hover:bg-gray-100" 
                onClick={() => {
                  setShowCreate(false);
                  setShowEditModal(false);
                  setSelectedBatch(null);
                  resetForm();
                }}
              >
                ✕
              </button>
            </div>
            <form onSubmit={showEditModal ? editBatch : createBatch} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business ID</label>
                  <input value={bizId || ""} readOnly className="w-full px-3 py-2 border rounded-lg bg-gray-50" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Batch Number {!showEditModal && "*"}
                  </label>
                  <input
                    value={form.batchNumber}
                    onChange={(e) => setForm({ ...form, batchNumber: e.target.value })}
                    required={!showEditModal}
                    readOnly={showEditModal}
                    className={`w-full px-3 py-2 border rounded-lg ${showEditModal ? 'bg-gray-50' : ''}`}
                    placeholder="e.g., BT-2025-001 (optional - will use batch name if empty)"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch Name *</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., January Run"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                  <input
                    type="number"
                    min={1}
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                  <select
                    value={form.productId}
                    onChange={(e) => setForm({ ...form, productId: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-lg"
                    disabled={loadingProducts || !products.length}
                  >
                    <option value="">
                      {loadingProducts 
                        ? "Loading products..." 
                        : products.length 
                          ? "Select product..." 
                          : "No products found"}
                    </option>
                    {products.map((p) => (
                      <option key={p.productId} value={p.productId}>
                        {p.name} ({p.sku})
                      </option>
                    ))}
                  </select>
                  {!loadingProducts && !products.length && (
                    <p className="text-xs text-red-500 mt-1">
                      No products found. Please create products first or check your business ID.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign Store *</label>
                  <select
                    value={form.assignedStoreId}
                    onChange={(e) => setForm({ ...form, assignedStoreId: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-lg"
                    disabled={loadingStores || !stores.length}
                  >
                    <option value="">
                      {loadingStores 
                        ? "Loading stores..." 
                        : stores.length 
                          ? "Select store..." 
                          : "No stores found"}
                    </option>
                    {stores
                      .filter((s) => s.isActive)
                      .map((s) => (
                        <option key={s.storeId} value={s.storeId}>
                          {s.storeName}
                          {s.city ? ` (${s.city})` : ""}
                        </option>
                      ))}
                  </select>
                  {!loadingStores && !stores.length && (
                    <p className="text-xs text-red-500 mt-1">
                      No stores found. Please create stores first or check your business ID.
                    </p>
                  )}
                </div>
              </div>

              {!showEditModal && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturing Date *</label>
                      <input
                        type="datetime-local"
                        value={form.manufacturingDate}
                        onChange={(e) => setForm({ ...form, manufacturingDate: e.target.value })}
                        required
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End of Coverage</label>
                      <div className="flex items-center gap-2 mb-2">
                        <button
                          type="button"
                          onClick={() => setExpiryMode("expiry")}
                          className={`px-3 py-1 rounded border ${
                            expiryMode === "expiry" ? "bg-blue-600 text-white border-blue-600" : "hover:bg-gray-50"
                          }`}
                        >
                          Expiry
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpiryMode("warranty")}
                          className={`px-3 py-1 rounded border ${
                            expiryMode === "warranty" ? "bg-blue-600 text-white border-blue-600" : "hover:bg-gray-50"
                          }`}
                        >
                          Warranty (months)
                        </button>
                      </div>
                      {expiryMode === "expiry" ? (
                        <input
                          type="datetime-local"
                          value={form.expiryDate}
                          onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
                          required
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      ) : (
                        <input
                          type="number"
                          min={1}
                          value={form.warrantyMonths}
                          onChange={(e) => setForm({ ...form, warrantyMonths: e.target.value })}
                          required
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder="e.g., 12"
                        />
                      )}
                    </div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Factory / City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <input
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Extra details"
                  />
                </div>
              </div>

              {/* ⛓ Blockchain Trace URL ────────────────────────────── */}
              <div className="border-t pt-4 mt-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ⛓ Blockchain Trace URL
                  <span className="ml-2 text-xs font-normal text-gray-500">(optional)</span>
                </label>
                <input
                  type="url"
                  value={form.traceUrl}
                  onChange={(e) => setForm({ ...form, traceUrl: e.target.value })}
                  disabled={showEditModal && selectedBatch?.status !== 'created'}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    showEditModal && selectedBatch?.status !== 'created'
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : ''
                  }`}
                  placeholder="https://your-system.com/trace/batch-123"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {showEditModal && selectedBatch?.status !== 'created' ? (
                    <span className="text-purple-700">
                      🔒 URL is locked. Cannot be changed after activation (anchored on blockchain).
                    </span>
                  ) : (
                    <>
                      Link to your batch's traceability page on your own system.
                      JepTags will lock this URL on Polygon blockchain at activation.
                    </>
                  )}
                </p>
              </div>
              {/* ──────────────────────────────────────────────────── */}

              {showEditModal && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value as BatchStatus })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {(["created", "production", "quality_check", "approved", "shipped", "delivered", "recalled"] as BatchStatus[]).map(
                      (s) => (
                        <option key={s} value={s}>
                          {s.replace("_", " ")}
                        </option>
                      )
                    )}
                  </select>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <button 
                  type="button" 
                  className="px-4 py-2 rounded border" 
                  onClick={() => {
                    setShowCreate(false);
                    setShowEditModal(false);
                    setSelectedBatch(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                  disabled={loadingProducts || loadingStores || !products.length || !stores.length}
                >
                  {showEditModal ? "Update Batch" : "Create Batch"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Drawer */}
      {selectedBatch && !showEditModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => { setSelectedBatch(null); setDetailTab("info"); }}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-xl font-semibold">Batch Details</h3>
              <button className="p-2 rounded hover:bg-gray-100" onClick={() => { setSelectedBatch(null); setDetailTab("info"); }}>
                ✕
              </button>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-4 flex gap-1 border-b">
              <button
                onClick={() => setDetailTab("info")}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                  detailTab === "info"
                    ? "border-blue-600 text-blue-600 bg-blue-50"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Package className="w-4 h-4 inline mr-1.5" />
                Batch Info
              </button>
              <button
                onClick={() => setDetailTab("quality")}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                  detailTab === "quality"
                    ? "border-blue-600 text-blue-600 bg-blue-50"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Award className="w-4 h-4 inline mr-1.5" />
                Quality &amp; Documents
              </button>
            </div>

            {/* Tab Content: Info */}
            {detailTab === "info" && (
              <div className="p-6 space-y-4 text-sm">
                <InfoRow label="Batch ID" value={<code>{selectedBatch.batchId}</code>} />
                <InfoRow label="Name" value={selectedBatch.name || "—"} />
                <InfoRow label="Batch Number" value={<code>{selectedBatch.batchNumber}</code>} />
                <InfoRow label="Product" value={selectedBatch.product?.name || selectedBatch.productId || "—"} />
                <InfoRow label="Store" value={selectedBatch.assignedStore?.storeName || "Unassigned"} />
                <InfoRow
                  label="Status"
                  value={<span className={`px-2 py-1 rounded-full ${statusColor(selectedBatch.status)}`}>{selectedBatch.status}</span>}
                />
                <InfoRow label="Quantity" value={`${selectedBatch.quantity?.toLocaleString()} units`} />
                <InfoRow label="Manufacturing" value={fmtDate(selectedBatch.manufacturingDate)} />
                <InfoRow label="Expiry" value={fmtDate(selectedBatch.expiryDate)} />
                <InfoRow label="Location" value={selectedBatch.location || "—"} />
                <InfoRow label="Tags" value={`${selectedBatch.activatedTags || 0} / ${selectedBatch.totalTags || 0}`} />

                <div className="pt-3 border-t flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setForm({
                        name: selectedBatch.name || "",
                        batchNumber: selectedBatch.batchNumber || "",
                        productId: selectedBatch.productId || "",
                        assignedStoreId: selectedBatch.assignedStoreId || "",
                        quantity: String(selectedBatch.quantity || 1),
                        manufacturingDate: selectedBatch.manufacturingDate ? new Date(selectedBatch.manufacturingDate).toISOString().slice(0, 16) : "",
                        expiryDate: selectedBatch.expiryDate ? new Date(selectedBatch.expiryDate).toISOString().slice(0, 16) : "",
                        location: selectedBatch.location || "",
                        notes: selectedBatch.notes || "",
                        traceUrl: selectedBatch.blockchain?.traceUrl || "",
                        status: selectedBatch.status,
                        warrantyMonths: "",
                      });
                      setShowEditModal(true);
                    }}
                    className="px-3 py-2 border rounded inline-flex items-center gap-2 hover:bg-gray-50"
                  >
                    <Edit className="w-4 h-4" /> Edit Batch
                  </button>
                  <Link
                    to={`/business/tags?batchId=${encodeURIComponent(selectedBatch.batchId)}&productId=${encodeURIComponent(
                      selectedBatch.product?.productId || selectedBatch.productId || ''
                    )}&storeId=${encodeURIComponent(selectedBatch.assignedStore?.storeId || selectedBatch.assignedStoreId || "")}`}
                    className="px-3 py-2 border rounded inline-flex items-center gap-2 hover:bg-gray-50"
                  >
                    <QrCode className="w-4 h-4" /> Tags
                  </Link>
                  <Link
                    to={`/business/supply-chain?batchId=${encodeURIComponent(selectedBatch.batchId)}&productId=${encodeURIComponent(
                      selectedBatch.product?.productId || selectedBatch.productId || ''
                    )}`}
                    className="px-3 py-2 border rounded inline-flex items-center gap-2 hover:bg-gray-50"
                  >
                    <Truck className="w-4 h-4" /> Supply Chain
                  </Link>
                  <Link
                    to={`/business/recall-control?batchId=${encodeURIComponent(selectedBatch.batchId)}`}
                    className="px-3 py-2 border rounded inline-flex items-center gap-2 hover:bg-gray-50"
                  >
                    <ShieldAlert className="w-4 h-4" /> Recall Control
                  </Link>
                  <button
                    onClick={() => deleteBatch(selectedBatch.batchId)}
                    className="px-3 py-2 border rounded inline-flex items-center gap-2 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" /> Delete Batch
                  </button>
                </div>
              </div>
            )}

            {/* Tab Content: Quality & Documents */}
            {detailTab === "quality" && (
              <div className="p-6 space-y-6 text-sm">

                {/* Certificates Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <Award className="w-5 h-5 text-amber-600" />
                      Certificates
                    </h4>
                    <button
                      onClick={() => setShowCertUpload(true)}
                      className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 inline-flex items-center gap-1"
                    >
                      <Upload className="w-3.5 h-3.5" /> Upload Certificate
                    </button>
                  </div>

                  {loadingDocs ? (
                    <div className="text-center py-4 text-gray-500">Loading...</div>
                  ) : certificates.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed">
                      <Award className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">No certificates uploaded yet</p>
                      <button
                        onClick={() => setShowCertUpload(true)}
                        className="mt-2 text-blue-600 text-xs hover:underline"
                      >
                        Upload your first certificate
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {certificates.map((cert) => (
                        <div key={cert._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border hover:border-blue-200 transition-colors">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                              <FileText className="w-4 h-4 text-amber-700" />
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900 truncate">{cert.name}</div>
                              <div className="text-xs text-gray-500 flex flex-wrap gap-x-3">
                                <span className="inline-flex items-center gap-1">
                                  <span className="bg-gray-200 px-1.5 py-0.5 rounded text-xs">{cert.type}</span>
                                </span>
                                {cert.certificateNumber && <span>#{cert.certificateNumber}</span>}
                                {cert.issuedBy && <span>by {cert.issuedBy}</span>}
                                {cert.expiryDate && (
                                  <span className={new Date(cert.expiryDate) < new Date() ? "text-red-500" : ""}>
                                    Exp: {new Date(cert.expiryDate).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0 ml-2">
                            {cert.verified ? (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium inline-flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Verified
                              </span>
                            ) : (
                              <button
                                onClick={() => toggleCertVerified(cert._id)}
                                className="px-2 py-0.5 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium hover:bg-yellow-100 inline-flex items-center gap-1"
                                title="Click to verify"
                              >
                                <Clock className="w-3 h-3" /> Unverified
                              </button>
                            )}
                            <a
                              href={cert.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 rounded hover:bg-gray-200 text-gray-500"
                              title="Download"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </a>
                            <button
                              onClick={() => deleteCertificate(cert._id)}
                              className="p-1.5 rounded hover:bg-red-100 text-red-500"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Divider */}
                <hr />

                {/* Inspection Reports Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <ClipboardCheck className="w-5 h-5 text-indigo-600" />
                      Inspection Reports
                    </h4>
                    <button
                      onClick={() => setShowInspUpload(true)}
                      className="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 inline-flex items-center gap-1"
                    >
                      <Upload className="w-3.5 h-3.5" /> Upload Report
                    </button>
                  </div>

                  {inspections.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed">
                      <ClipboardCheck className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">No inspection reports uploaded yet</p>
                      <button
                        onClick={() => setShowInspUpload(true)}
                        className="mt-2 text-indigo-600 text-xs hover:underline"
                      >
                        Upload your first report
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {inspections.map((insp) => (
                        <div key={insp._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border hover:border-indigo-200 transition-colors">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                              <ClipboardCheck className="w-4 h-4 text-indigo-700" />
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900">
                                Inspection{insp.inspector ? ` by ${insp.inspector}` : ""}
                              </div>
                              <div className="text-xs text-gray-500 flex flex-wrap gap-x-3">
                                {insp.result && (
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                                    insp.result === "pass" ? "bg-green-100 text-green-700" :
                                    insp.result === "fail" ? "bg-red-100 text-red-700" :
                                    "bg-yellow-100 text-yellow-700"
                                  }`}>
                                    {insp.result.toUpperCase()}
                                  </span>
                                )}
                                {insp.inspectionDate && (
                                  <span>{new Date(insp.inspectionDate).toLocaleDateString()}</span>
                                )}
                                {insp.notes && <span className="truncate max-w-[150px]">{insp.notes}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0 ml-2">
                            <a
                              href={insp.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 rounded hover:bg-gray-200 text-gray-500"
                              title="Download"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </a>
                            <button
                              onClick={() => deleteInspection(insp._id)}
                              className="p-1.5 rounded hover:bg-red-100 text-red-500"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Certificate Upload Modal */}
      {showCertUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setShowCertUpload(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" /> Upload Certificate
              </h3>
              <button className="p-2 rounded hover:bg-gray-100" onClick={() => setShowCertUpload(false)}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* File input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
                <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <FileUp className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {certFile ? certFile.name : "Choose PDF or image file..."}
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => setCertFile(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Name *</label>
                  <input
                    value={certForm.name}
                    onChange={(e) => setCertForm({ ...certForm, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="e.g., ISO 9001"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    value={certForm.type}
                    onChange={(e) => setCertForm({ ...certForm, type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="ISO">ISO</option>
                    <option value="organic">Organic</option>
                    <option value="halal">Halal</option>
                    <option value="kosher">Kosher</option>
                    <option value="fair_trade">Fair Trade</option>
                    <option value="GMP">GMP</option>
                    <option value="HACCP">HACCP</option>
                    <option value="FDA">FDA</option>
                    <option value="CE">CE</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Certificate #</label>
                  <input
                    value={certForm.certificateNumber}
                    onChange={(e) => setCertForm({ ...certForm, certificateNumber: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="ISO-2025-001234"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issued By</label>
                  <input
                    value={certForm.issuedBy}
                    onChange={(e) => setCertForm({ ...certForm, issuedBy: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="Bureau Veritas"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                  <input
                    type="date"
                    value={certForm.issueDate}
                    onChange={(e) => setCertForm({ ...certForm, issueDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                  <input
                    type="date"
                    value={certForm.expiryDate}
                    onChange={(e) => setCertForm({ ...certForm, expiryDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={certForm.notes}
                  onChange={(e) => setCertForm({ ...certForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  rows={2}
                  placeholder="Optional notes..."
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowCertUpload(false)}
                  className="px-4 py-2 rounded border text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={uploadCertificate}
                  disabled={!certFile || !certForm.name || uploading}
                  className="px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" /> Upload Certificate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inspection Upload Modal */}
      {showInspUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setShowInspUpload(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-indigo-600" /> Upload Inspection Report
              </h3>
              <button className="p-2 rounded hover:bg-gray-100" onClick={() => setShowInspUpload(false)}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report File *</label>
                <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <FileUp className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {inspFile ? inspFile.name : "Choose PDF or image file..."}
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => setInspFile(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inspector</label>
                  <input
                    value={inspForm.inspector}
                    onChange={(e) => setInspForm({ ...inspForm, inspector: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="Inspector name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Date</label>
                  <input
                    type="date"
                    value={inspForm.inspectionDate}
                    onChange={(e) => setInspForm({ ...inspForm, inspectionDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Result *</label>
                <select
                  value={inspForm.result}
                  onChange={(e) => setInspForm({ ...inspForm, result: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="pass">Pass</option>
                  <option value="fail">Fail</option>
                  <option value="conditional">Conditional</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={inspForm.notes}
                  onChange={(e) => setInspForm({ ...inspForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  rows={2}
                  placeholder="Optional notes..."
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowInspUpload(false)}
                  className="px-4 py-2 rounded border text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={uploadInspection}
                  disabled={!inspFile || uploading}
                  className="px-4 py-2 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" /> Upload Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================ Small components ============================ */
const StatCard: React.FC<{ label: string; value: string | number; icon: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm border">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-600">{label}</div>
      </div>
      {icon}
    </div>
  </div>
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-2">
    <div className="text-gray-500">{label}</div>
    <div className="col-span-2 text-gray-900">{value}</div>
  </div>
);

export default BatchesPage;// Wed Mar 25 19:36:25 PDT 2026